import React, {
  useEffect,
  useState,
  useCallback,
  createContext,
  useMemo,
  useRef,
  useContext,
} from 'react';
import { useParams } from 'react-router';
import { parseISO } from 'date-fns';
import { TourItemType } from '@bb/domain/dist/types';
import { OnboardingPortalAssociation } from '@bb/automaton';
import errorReporter from '@app/errorReporter';
import { CoopCancelReasonType } from '@app/DashboardComponents/CoopCancelAppointmentComponent';
import { message } from 'components';
import { featureEnabled, formatISODateStr } from 'utils';
import {
  addStopToTour,
  addListingToTour,
  bookAppointmentsForTour,
  deleteTourItem,
  getTourContent,
} from 'api/TourController';
import {
  coopDenyTimeChange,
  setStatusByCoop,
  timeChange,
} from 'api/AppointmentController';
import { useBreakpoint } from 'hooks';
import { getAssocMapByOrgsAndPortalId } from 'api/OnboardingPortalController';
import { uniq } from 'lodash';
import { AuthContext } from 'contexts';
import { formatInTimeZone, zonedTimeToUtc } from 'date-fns-tz';
import {
  OptimizeStatus,
  RouteInfo,
  Tour,
  TourItemContent,
  TourAutoSaveStatus,
  StatusUpdateType,
  SendAppointmentResponse,
  TourCancelType,
} from './types';

import { useTourAutoSave, useFloatingPanelAnchors } from './hooks';
import showConfirmDeleteTourModal from './ConfirmDeleteTourModal';
import showTimeNoLongerAvailableModal from './TimeNoLongerAvailableModal';
import { EnhancedFloatingPanelRef } from './EnhancedFloatingPanel';

type TourEditContextType = {
  // Status Updates
  loading: boolean;
  autoSaveStatus: TourAutoSaveStatus;
  optimizeStatus: OptimizeStatus;
  setOptimizeStatus: React.Dispatch<React.SetStateAction<OptimizeStatus>>;
  // States
  tour: Tour;
  setTour: React.Dispatch<React.SetStateAction<Tour>>;
  tourItems: TourItemContent[];
  setTourItems: React.Dispatch<React.SetStateAction<TourItemContent[]>>;
  cancelledTourItems: TourItemContent[];
  routeInfo: Record<string, RouteInfo>;
  setRouteInfo: React.Dispatch<React.SetStateAction<Record<string, RouteInfo>>>;
  travelMode: google.maps.TravelMode;
  setTravelMode: React.Dispatch<React.SetStateAction<google.maps.TravelMode>>;
  optimizeFlag: boolean;
  setOptimizeFlag: React.Dispatch<React.SetStateAction<boolean>>;
  isSorting: boolean;
  setIsSorting: React.Dispatch<React.SetStateAction<boolean>>;
  hasNarBuyerSettlement: boolean;
  preBookingModalItem: TourItemContent | undefined;
  // Functions
  cancelCurrentTour: () => Promise<void>;
  optimizeTour: () => void;
  setTourItemTime: (
    beginTime: Date,
    endTime: Date,
    id: string,
  ) => Promise<void>;
  setTourNote: (note: string, id: string) => Promise<void>;
  addSelectedListingToTour: (listingId: string) => Promise<void>;
  addSelectedStopToTour: (
    stop: Pick<TourItemContent, 'address' | 'locationName' | 'coordinates'>,
  ) => Promise<void>;
  removeItemFromTour: (itemId: string) => Promise<void>;
  sendAppointmentRequests: (
    sendStatusUpdateTo: StatusUpdateType,
  ) => Promise<SendAppointmentResponse>;
  cancelSubmittedAppointment: (
    appointmentId: string,
    reason: CoopCancelReasonType,
    details: string,
  ) => Promise<void>;
  appointmentTimeChange: (
    appointmentId: string,
    options: { date: string; time: Date; timeEnd: Date },
  ) => Promise<void>;
  acceptSuggestedTime: (appointmentId: string) => Promise<void>;
  denySuggestedTime: (appointmentId: string) => void;
  mobileSidenavRef: React.MutableRefObject<EnhancedFloatingPanelRef>;
  modalFooterRef: React.MutableRefObject<HTMLDivElement>;
  prebookingOnOk: () => void;
  prebookingOnClose: () => Promise<void>;
};

const TourEditContext = createContext<TourEditContextType>(null);

export const TourEditContextProvider: React.FC = ({ children }) => {
  const { tourId } = useParams<{ tourId: string }>();

  // Status Updates
  const [loading, setLoading] = useState(true);
  const [optimizeStatus, setOptimizeStatus] = useState<OptimizeStatus>(
    OptimizeStatus.NOT_OPTIMIZED,
  );

  // States
  const [tour, setTour] = useState<Tour | null>();
  const [tourItems, setTourItems] = useState<TourItemContent[]>([]);
  const [cancelledTourItems, setCancelledTourItems] = useState<
    TourItemContent[]
  >([]);
  const [routeInfo, setRouteInfo] = useState<Record<string, RouteInfo>>({});
  const [travelMode, setTravelMode] = useState<google.maps.TravelMode>(
    google.maps.TravelMode.DRIVING,
  );
  const [optimizeFlag, setOptimizeFlag] = useState(false);
  const [isSorting, setIsSorting] = useState<boolean>(false);
  const [narBuyerSettlementMap, setNarBuyerSettlementMap] = useState<
    Record<string, boolean>
  >({});
  const [hasNarBuyerSettlement, setHasNarBuyerSettlement] = useState<boolean>();
  const [preBookingModalItem, setPreBookingModalItem] =
    useState<TourItemContent>();

  // contexts
  const { auth, hasFeature } = useContext(AuthContext);

  // hooks
  const { md } = useBreakpoint();
  const floatingPanelAnchors = useFloatingPanelAnchors();

  // Refs
  const mobileSidenavRef = useRef<EnhancedFloatingPanelRef>();
  const modalFooterRef = useRef(null);
  const waitForAutoSaveRef = useRef<Promise<void>>();

  // Functions

  // # Region Private
  // Private
  const addItemToTourContext = useCallback(
    (itemToAdd: TourItemContent) => {
      setTourItems(currTourItems => {
        const nextTourItems: TourItemContent[] = currTourItems.map(item => {
          if (!item.nextItem) {
            return { ...item, nextItem: itemToAdd.id };
          }

          return item;
        });

        return [...nextTourItems, itemToAdd];
      });
      setOptimizeStatus(OptimizeStatus.NOT_OPTIMIZED);
    },
    [setTourItems],
  );

  const moveItemToCancelled = useCallback(
    (itemToRemove: TourItemContent, toCancelled?: boolean) => {
      const newList: TourItemContent[] = [];
      tourItems.forEach(item => {
        if (item.id === itemToRemove.id) return;

        const newItem = { ...item };
        if (newItem.nextItem === itemToRemove.id) {
          newItem.nextItem = itemToRemove.nextItem;
        }
        newList.push(newItem);
      });
      // In case removed appt was the first item
      if (newList.length) newList[0].isFirstItem = true;

      setTourItems(newList);
      if (toCancelled) {
        setCancelledTourItems(prev => [
          ...prev,
          {
            ...itemToRemove,
            status: 'cancel',
            nextItem: null,
            displayStatus: 'Cancelled',
          },
        ]);
      }
      setOptimizeStatus(OptimizeStatus.NOT_OPTIMIZED);
    },
    [tourItems],
  );

  // # Region Public
  const cancelCurrentTour = async () => {
    try {
      const cancelableItemCount = tourItems.reduce(
        (count, tourItem) =>
          tourItem.appointmentId && tourItem.status !== 'time_passed'
            ? count + 1
            : count,
        0,
      );
      showConfirmDeleteTourModal(
        tourId,
        cancelableItemCount > 0
          ? TourCancelType.WITH_SUBMITTED_SHOWING
          : TourCancelType.WITHOUT_SUBMITTED_SHOWING,
      );
    } catch (err) {
      message.error('Error canceling the tour');
      errorReporter.error(err);
    }
  };

  const optimizeTour = () => {
    // move mobile sidenav floating panel to lowest anchor point when route is optimized
    if (!md) mobileSidenavRef.current.setHeight(floatingPanelAnchors[0]);
    if (optimizeStatus === OptimizeStatus.OPTIMIZING) return;
    if (optimizeStatus === OptimizeStatus.OPTIMIZED) {
      message.success('Shortest route found. Review your schedule');
      return;
    }
    setOptimizeStatus(OptimizeStatus.OPTIMIZING);
    setOptimizeFlag(true);
  };

  const addSelectedStopToTour = useCallback(
    async (
      stop: Pick<TourItemContent, 'address' | 'locationName' | 'coordinates'>,
    ) => {
      try {
        const {
          id,
          address,
          locationName,
          coordinates,
          type,
          timezone,
          appointmentTourItemId,
        } = await addStopToTour(tourId, {
          address: stop.address,
          locationName: stop.locationName,
          coordinates: stop.coordinates,
        });

        const stopsToAdd: TourItemContent = {
          id: appointmentTourItemId,
          tourItemId: id,
          address,
          locationName,
          coordinates,
          type,
          timezone,
          nextItem: null,
        };

        addItemToTourContext(stopsToAdd);
      } catch (err) {
        message.error('Error adding route stop to tour');
        errorReporter.error(err);
      }
    },
    [addItemToTourContext, tourId],
  );

  const addSelectedListingToTour = useCallback(
    async (listingId: string) => {
      try {
        const {
          id,
          address,
          autoConfirm,
          coordinates,
          mls_numbers,
          thumbnail,
          timezone,
          appointmentTourItemId,
          time,
          timeEnd,
          preBookingInformation,
          ...enhancedListing
        } = await addListingToTour(tourId, listingId);

        const listingToAdd: TourItemContent = {
          ...enhancedListing,
          id: appointmentTourItemId,
          tourItemId: id,
          address,
          locationName: address,
          coordinates: coordinates as [number, number],
          mls_numbers,
          nextItem: null,
          thumbnail,
          timezone,
          autoConfirm,
          type: TourItemType.APPOINTMENT,
          listingId,
          // @ts-expect-error: preBookingInformation is not existing in TourItemContentDTO
          preBookingInformation,
          ...(time && new Date(time)),
          ...(timeEnd && new Date(timeEnd)),
        };
        addItemToTourContext(listingToAdd);

        if (!listingToAdd.coordinates) {
          message.warn(
            'The added listing cannot be displayed on the map due to invalid coordinates.',
          );
        }

        if (
          preBookingInformation !== undefined &&
          preBookingInformation !== '' &&
          featureEnabled('preBookingToursNotificationFeature') &&
          hasFeature('PRE_BOOKING')
        ) {
          setPreBookingModalItem(listingToAdd);
        }
      } catch (err) {
        const { message: errorMessage } = err.response?.data || {
          message: 'Error adding listing to tour',
        };
        message.warn(errorMessage);
        errorReporter.error(err);
      }
    },
    [tourId, addItemToTourContext],
  );

  const removeItemFromTour = async (itemId: string) => {
    try {
      const itemToRemove = tourItems.find(({ id }) => id === itemId);
      // Shouldn't reach here
      if (!itemToRemove?.tourItemId) return;

      // Delete tour item for all lengths
      if (tourItems.length <= 1) {
        showConfirmDeleteTourModal(tourId, TourCancelType.LAST_ITEM);
        return;
      }

      await deleteTourItem(tourId, itemToRemove.tourItemId);

      moveItemToCancelled(itemToRemove);
    } catch (err) {
      message.error('Error fetching tour data');
      errorReporter.error(err);
    }
  };

  const cancelSubmittedAppointment = async (
    appointmentId: string,
    reason: CoopCancelReasonType,
    details: string,
  ) => {
    try {
      if (tourItems.length <= 1) {
        showConfirmDeleteTourModal(
          tourId,
          TourCancelType.LAST_ITEM,
          details,
          reason,
        );
        return;
      }

      // Try to cancel the appt anyways even if the item was missing
      await setStatusByCoop(appointmentId, { status: 'cancel', details });

      const itemToRemove = tourItems.find(
        ({ appointmentId: tourApptId }) => tourApptId === appointmentId,
      );
      if (!itemToRemove) return;

      moveItemToCancelled(itemToRemove, true);

      message.success('Showing cancelled');
    } catch (err) {
      message.error('Error cancelling the appointment');
      errorReporter.error(err);
    }
  };

  // On TourItems 1) add 2) delete 3) re-order,
  // Run setOptimizeStatus(OptimizeStatus.NOT_OPTIMIZED);
  // Instead of listening to tourItems, since other changes to the array
  // should not cause the optimize status to change

  const setTourItemTime = useCallback(
    async (beginTime: Date, endTime: Date, id: string) => {
      try {
        const itemToModify = tourItems.find(({ id: itemId }) => itemId === id);
        if (!itemToModify?.tourItemId) return;
        setTourItems(curTourItems => {
          const newState: TourItemContent[] = [];
          curTourItems.forEach(tour_ => {
            if (tour_.id === id && tour_.type === TourItemType.APPOINTMENT) {
              newState.push({ ...tour_, timeEnd: endTime, time: beginTime });
            } else {
              newState.push(tour_);
            }
          });
          return newState;
        });
      } catch (err) {
        message.error('Cannot set tour item time.');
        errorReporter.error(err);
      }
    },
    [tourItems],
  );

  const setTourNote = useCallback(
    async (note: string, id: string) => {
      try {
        const itemToModify = tourItems.find(({ id: itemId }) => itemId === id);
        if (!itemToModify?.tourItemId) return;
        setTourItems(curTourItems => {
          const newState: TourItemContent[] = [];
          curTourItems.forEach(tour_ => {
            if (tour_.id === id && tour_.type === TourItemType.APPOINTMENT) {
              newState.push({ ...tour_, note });
            } else {
              newState.push(tour_);
            }
          });
          return newState;
        });
      } catch (err) {
        message.error('Cannot set tour item note.');
        errorReporter.error(err);
      }
    },
    [tourItems],
  );

  const fetchTour = useCallback(
    async (isInitialFetch?: boolean) => {
      try {
        const {
          tourItems: tourItemsData,
          cancelledTourItems: cancelledTourItemsData,
          ...tourData
        } = await getTourContent(tourId);
        // Tour has an item with unavailable time
        const itemsWithUnavailableTime: TourItemContent[] = [];
        const newTourItemsData = tourItemsData.map(tourItem_ => {
          if (tourItem_.timeNotAvailable) {
            itemsWithUnavailableTime.push(tourItem_);
            // set time and timeEnd to undefined so user can't send showing request if time is not available
            const newTourItem: TourItemContent = {
              ...tourItem_,
              time: undefined,
              timeEnd: undefined,
            };
            return newTourItem;
          }
          return tourItem_;
        });
        // Tour has an item with invalid coordinates
        const itemsWithoutCoords = tourItemsData.filter(
          item => !item.coordinates,
        );
        if (
          isInitialFetch &&
          (itemsWithUnavailableTime.length || itemsWithoutCoords.length)
        ) {
          showTimeNoLongerAvailableModal({
            itemsWithUnavailableTime,
            itemsWithoutCoords,
          });
        }

        const tourTimezone =
          tourItemsData.find(tourItem => Boolean(tourItem.timezone))
            ?.timezone ||
          auth.org.addresses[parseInt(auth.location, 10)][
            parseInt(auth.address, 10)
          ].timezone;

        const tourDateStr = formatInTimeZone(
          new Date(tourData.date),
          tourTimezone,
          'yyyy-MM-dd',
        );

        setTour({
          ...tourData,
          date: zonedTimeToUtc(parseISO(tourDateStr), tourTimezone),
        });
        setTourItems(newTourItemsData);
        setCancelledTourItems(cancelledTourItemsData);
        setLoading(false);
      } catch (err) {
        if (err.response.status === 404) {
          window.location.href = `/404.html`;
        }
        message.error('Error fetching tour data');
        errorReporter.error(err);
      }
    },
    [tourId],
  );

  const sendAppointmentRequests = async (
    sendStatusUpdatesTo: StatusUpdateType,
  ) => {
    try {
      const data = await bookAppointmentsForTour(sendStatusUpdatesTo, tourId);

      await fetchTour();

      return data;
    } catch (err) {
      message.error('Error sending appointment requests');
      errorReporter.error(err);
    }
  };

  const appointmentTimeChange = useCallback(
    async (appointmentId, options) => {
      try {
        const { displayStatus, status, isPending } = await timeChange(
          appointmentId,
          options,
          true,
        );

        const newList = tourItems.map(item => {
          if (item.appointmentId === appointmentId) {
            const newItem = {
              ...item,
              listingContactTimeChange: false,
              time: options.time,
              timeEnd: options.timeEnd,
              originalTime: item.time,
              originalTimeEnd: item.timeEnd,
              status,
              displayStatus,
              isPending,
            };
            return newItem;
          }
          return item;
        });
        message.success('Time Change Requested');
        setTourItems(newList);
      } catch (err) {
        message.error('Error appointment time change');
        errorReporter.error(err);
      }
    },
    [tourItems],
  );

  const acceptSuggestedTime = useCallback(
    async (appointmentId: string) => {
      try {
        const itemToAccept = tourItems.find(
          item => item.appointmentId === appointmentId,
        );
        if (
          !itemToAccept?.listingContactTimeChange ||
          itemToAccept.status !== 'time_change'
        ) {
          return;
        }

        const acceptTourItem = () => {
          const newTourItems = tourItems.map(tourItem => {
            const newItem = { ...tourItem };
            if (tourItem.id === itemToAccept.id) newItem.status = 'confirm';
            return newItem;
          });
          setTourItems(newTourItems);
        };

        const { time, originalTime, timezone } = itemToAccept;
        const dateStr = formatISODateStr(time, timezone);
        const originalDateStr = formatISODateStr(originalTime, timezone);
        const isLastShowing =
          tourItems.filter(
            item => item.type !== TourItemType.ROUTE_STOP && item.time,
          ).length <= 1;
        // 1. Changing to a different date, this is the only showing type tour item.
        if (dateStr !== originalDateStr && isLastShowing) {
          showConfirmDeleteTourModal(
            tourId,
            TourCancelType.SUGGESTION_ACCEPT,
            undefined,
            undefined,
            async () => {
              await waitForAutoSaveRef.current;
              await setStatusByCoop(appointmentId, { status: 'confirm' });
              acceptTourItem();
              setTour(prevTour => ({
                ...prevTour,
                date: zonedTimeToUtc(
                  parseISO(
                    formatInTimeZone(new Date(time), timezone, 'yyyy-MM-dd'),
                  ),
                  timezone,
                ),
              }));

              message.success('Suggested time accepted');
            },
          );
          return;
        }
        // make sure changes are saved, otherwise the item might be handled differently by BE
        await waitForAutoSaveRef.current;
        await setStatusByCoop(appointmentId, { status: 'confirm' });

        if (dateStr !== originalDateStr && !isLastShowing) {
          // 2. Changing to a different date, there are multiple tour items.
          // Remove appointment from current tour
          moveItemToCancelled(itemToAccept);

          message.success(
            <span>
              Suggested time accepted. See it in your showings{' '}
              <a href={'#/my_business/showings/buyer'}>here</a>
            </span>,
            6,
          );
        } else {
          // 3. Accepting same day time suggestion.
          acceptTourItem();
          message.success('Suggested time accepted');
        }
      } catch (err) {
        message.error('Error accepting suggested time change');
        errorReporter.error(err);
      }
    },
    [tourItems, tourId, moveItemToCancelled],
  );

  const denySuggestedTime = useCallback(
    (appointmentId: string) => {
      try {
        const itemToDeny = tourItems.find(
          item => item.appointmentId === appointmentId,
        );
        if (
          !itemToDeny?.listingContactTimeChange ||
          itemToDeny.status !== 'time_change'
        ) {
          return;
        }

        let cancelType = TourCancelType.SUGGESTION_CANCEL;
        if (itemToDeny.keepOriginalAppointment) {
          cancelType = TourCancelType.SUGGESTION_DENY;
        } else if (tourItems.length <= 1) {
          cancelType = TourCancelType.SUGGESTION_CANCEL_LAST;
        }

        const onConfirm = async () => {
          await coopDenyTimeChange(appointmentId, {
            notify: itemToDeny.notify,
            isPending: false,
          });

          if (cancelType === TourCancelType.SUGGESTION_CANCEL) {
            moveItemToCancelled(itemToDeny, true);
            message.success(
              'Suggested time denied. The original showing has been cancelled.',
            );
          } else if (cancelType === TourCancelType.SUGGESTION_DENY) {
            const newList = tourItems.map(item => {
              if (item.appointmentId !== appointmentId) return item;
              return {
                ...item,
                status: itemToDeny.originalStatus,
                time: itemToDeny.originalTime,
                timeEnd: itemToDeny.originalTimeEnd,
              };
            });
            setTourItems(newList);

            message.success(
              'Suggested time denied. The original showing has been kept.',
            );
          }
        };

        showConfirmDeleteTourModal(
          tourId,
          cancelType,
          undefined,
          undefined,
          onConfirm,
        );
      } catch (err) {
        message.error('Error denying suggested time change');
        errorReporter.error(err);
      }
    },
    [tourItems, tourId, moveItemToCancelled],
  );

  const prebookingOnOk = () => {
    setPreBookingModalItem(undefined);
  };

  const prebookingOnClose = async () => {
    if (!preBookingModalItem) return;
    await removeItemFromTour(preBookingModalItem.id);
    setPreBookingModalItem(undefined);
  };

  // Effects
  useEffect(() => {
    fetchTour(true);
  }, [fetchTour]);

  useEffect(() => {
    let key: string;
    if (optimizeStatus === OptimizeStatus.OPTIMIZING) {
      key = 'optimizing_route';
      message.loading({ content: 'Finding shortest route', key });
    } else if (optimizeStatus === OptimizeStatus.OPTIMIZED) {
      key = 'optimized';
      message.success({
        content: 'Shortest route found. Review your schedule',
        key,
      });
    }

    return () => {
      if (key) {
        message.destroy(key);
      }
    };
  }, [optimizeStatus]);

  useEffect(() => {
    // This new updateNarBuyerSettlementValue logic enhances loading time of the page by:
    // Case 1: One portal, one org listings
    // Page Loading time - Before: ~5.1s, After: ~2.7s
    // Case 2: Multiple portal, multiple org listings
    // Page Loading time - Before: ~11s, After: ~3.7s
    const updateNarBuyerSettlementValue = async () => {
      const currentItemIds = new Set(tourItems.map(item => item.listingId));
      const updatedMap = { ...narBuyerSettlementMap };

      const newItems = tourItems.filter(
        item =>
          item.listingId !== undefined &&
          !(item.listingId in narBuyerSettlementMap),
      );

      const portalToListingIds: Record<
        string,
        { orgId: string; listingId: string }[]
      > = {};
      newItems.forEach(({ listingId, listingType, org }) => {
        if (!listingId) return;
        if (listingType && ['land', 'commercial'].includes(listingType)) {
          updatedMap[listingId] = false;
          return;
        }

        if (org?.onboardingPortalId && org?.id) {
          const { onboardingPortalId, id: orgId } = org;
          if (portalToListingIds[onboardingPortalId]) {
            portalToListingIds[onboardingPortalId].push({ orgId, listingId });
          } else {
            portalToListingIds[onboardingPortalId] = [{ orgId, listingId }];
          }
        }
      });

      const portalIds = Object.keys(portalToListingIds);

      try {
        const orgAssocMapsArray = await Promise.all(
          portalIds.map(portalId =>
            getAssocMapByOrgsAndPortalId({
              portalId,
              orgIds: uniq(
                portalToListingIds[portalId].map(({ orgId }) =>
                  orgId.toString(),
                ),
              ),
            }),
          ),
        );
        const orgAssocMaps = orgAssocMapsArray.reduce<
          Record<string, OnboardingPortalAssociation | undefined>
        >((acc, cur) => {
          Object.keys(cur).forEach(key => {
            acc[key] = cur[key];
          });

          return acc;
        }, {});

        newItems.forEach(({ listingId, listingType, org }) => {
          if (
            !listingId ||
            (listingType && ['land', 'commercial'].includes(listingType))
          )
            return;

          if (org?.onboardingPortalId && org?.id) {
            updatedMap[listingId] = Boolean(
              orgAssocMaps[org.id]?.narSettlementAgreement,
            );
          }
        });
      } catch (error) {
        console.error('Failed to get portal association:', error);
        // TODO: This logic was there from original, setHasNarBuyerSettlement will be called outside of this scope after this line,
        // please review if this is needed.
        setHasNarBuyerSettlement(true);
      }

      Object.keys(narBuyerSettlementMap).forEach(id => {
        if (!currentItemIds.has(id)) {
          delete updatedMap[id];
        }
      });

      setNarBuyerSettlementMap(updatedMap);
      setHasNarBuyerSettlement(Object.values(updatedMap).some(Boolean));
    };

    if (featureEnabled('narBuyerSettlementFeature')) {
      updateNarBuyerSettlementValue();
    }
  }, [tourItems]);

  // Custom Hooks
  // 1. Auto-save
  const { tourItemOrder, tourItemTimes, tourItemNotes } = useMemo(
    () => ({
      tourItemOrder: tourItems.map(({ id }) => id),
      tourItemTimes: tourItems
        .filter(({ tourItemId }) => tourItemId)
        .map(({ tourItemId, time, timeEnd }) => ({
          tourItemId,
          time,
          timeEnd,
        })),
      tourItemNotes: tourItems
        .filter(({ tourItemId }) => tourItemId)
        .map(({ tourItemId, note }) => ({ tourItemId, note })),
    }),
    [tourItems],
  );

  const autoSaveStatus = useTourAutoSave(
    tourId,
    loading
      ? null
      : {
          name: tour.name,
          date: tour.date,
          tourItemOrder,
          tourItemTimes,
          tourItemNotes,
          buyers: tour.buyers.map(({ value }) => value),
        },
    pendingBuyers => setTour(prev => ({ ...prev, pendingBuyers })),
  );

  const isSaved = autoSaveStatus === TourAutoSaveStatus.SAVED;
  useEffect(() => {
    if (!isSaved) {
      let resolveSync: () => void;
      waitForAutoSaveRef.current = new Promise<void>(resolve => {
        resolveSync = resolve;
      });
      return () => resolveSync();
    }
    waitForAutoSaveRef.current = Promise.resolve();
  }, [isSaved]);

  return (
    <TourEditContext.Provider
      value={{
        // Status Updates
        loading,
        autoSaveStatus,
        optimizeStatus,
        setOptimizeStatus,
        // States
        tour,
        setTour,
        tourItems,
        setTourItems,
        cancelledTourItems,
        routeInfo,
        setRouteInfo,
        travelMode,
        setTravelMode,
        optimizeFlag,
        setOptimizeFlag,
        isSorting,
        setIsSorting,
        preBookingModalItem,
        hasNarBuyerSettlement,
        // Functions
        cancelCurrentTour,
        optimizeTour,
        setTourItemTime,
        setTourNote,
        addSelectedListingToTour,
        addSelectedStopToTour,
        removeItemFromTour,
        cancelSubmittedAppointment,
        appointmentTimeChange,
        sendAppointmentRequests,
        acceptSuggestedTime,
        denySuggestedTime,
        mobileSidenavRef,
        modalFooterRef,
        prebookingOnOk,
        prebookingOnClose,
      }}
    >
      {children}
    </TourEditContext.Provider>
  );
};

export default TourEditContext;
